import { customElement, property, state } from "lit/decorators.js";
import { connect } from "pwa-helpers";
import store, { waitForCart, waitForUser, waitForValidateLoggedInUserConnectLicense } from "../../../store/store";
import { BaseComponent } from "../../base-component/base-component";
import { getCart as shopGetCart, getCartStatus } from "../shop-cart/store/shop-cart.selectors";
import { StoreState } from "../../../store/store.interface";
import { UserProfile } from "../../user-profile/user-profile.interface";
import { isLoggedOutUser, isSuccessUser, selectUser } from "../../user-profile/user-profile.selectors";
import { initializeCart } from "./thunk/initialize-cart.thunk";
import { getShopCart } from "../shop-cart/thunks/get-shop-cart.thunk";
import { ActionStatus } from "../../../common/constants/action-status";
import { html, nothing } from "lit";
import { doRedirect, isInitializeCartFailed, isInitializeCartSuccess, isValidateUserConnectLicenseActiveFailed, isValidateUserConnectLicenseActiveIdleOrLoading, isValidateUserConnectLicenseActiveSuccess, loginLink, validateUserConnectLicenseErrorMessage } from "./utils/initialize-cart.util";
import { getCartPageUrl } from "../../../common/utils/cart-util";
import { InitializeCartConstants } from "./constants/initialize-cart.constants";
import { PRICING_SUBJECT_INFO, smallSpinner } from "../../../common/constants/api-constants";
import { validateLoggedInUserConnectLicense } from "./thunk/validate-user-connect-license.thunk";
import { LoggedInUserValidateErrorConstants } from "./constants/validate-user-connect-license-error.constants";
import { booleanConverter } from "../../../common/converters";
import emit from "../../../common/events";
import { captureAddToCartAnalytics } from "../../../common/utils/analytics-util";
import { saveCartLocalStorageObject } from "../../search/utils/search.util";
import { getApiErrorMessage } from '../../../common/utils/error-message.util';

@customElement('mhecomm-initialize-cart')
export class InitializeCart extends connect(store)(BaseComponent) {

  /**
  * The isbn13 of the product.
  */
  @property()
  isbn13?: string;

  /**
  * The net price of the product.
  */
  @property({ type: Number })
  netPrice?: number;

  /**
  * Which store this product is for. This parameter is optional.
  */
  @property()
  store?: string;

  /**
  * The isbn of the product.
  */
  @property()
  isbn?: string;

  /**
  * The businessUnit of the product.
  */
  @property()
  businessUnit?: string;

  /**
  * The copyRightYear of the product.
  */
  @property()
  copyRightYear?: string;

  /**
  * The productTitle of the product.
  */
  @property()
  productTitle?: string;

  /**
  * The description of the product.
  */
  @property()
  description?: string;

  /**
  * The gradeLevel of the product.
  */
  @property()
  gradeLevel?: string;

  /**
  * The contributors of the product.
  */
  @property()
  contributors?: string;

  /**
  * The edition of the product.
  */
  @property({ type: Number })
  edition?: number;

  /**
  * The instructorOnly flag of the product.
  */
  @property({ converter: booleanConverter })
  showRestrictions = false;

  /**
  * The quantity of the product.
  */
  @property({ type: Number })
  quantity = 1;

  /**
  * The pdpType of the product.
  */
  @property()
  pdpType?: string;

  /**
   * Capture Event in local storage for analytics
   */
  @property({ converter: booleanConverter })
  capturePDPLocalStorage = false;

  @property()
  programSourceId?: string;

  @property({ converter: booleanConverter })
  everGreenProduct = false;

  @state()
  private stateIsDisabled = false;

  @state()
  private addedToCart = false;

  @state()
  private buttonText?: string;

  @state()
  private initializeCartSuccess = false;

  @state()
  private initializeCartFailed = false;

  @state()
  private isLoggedOut = false;

  @state()
  private validUserConnectLicense = true;

  @state()
  private validateUserErrorMessage?: string;

  @state()
  private isValidateUserIdleOrLoading = false;

  private user?: UserProfile;

  async connectedCallback() {
    super.connectedCallback();
    await waitForUser();
  }

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await store.dispatch(getShopCart({ store: this.store }));
    this.determineBtnText(store.getState() as StoreState);
    await store.dispatch(validateLoggedInUserConnectLicense(this.isbn13!));
    await waitForValidateLoggedInUserConnectLicense();
  }

  stateChanged(state: StoreState) {
    this.user = selectUser(state);
    this.isValidateUserIdleOrLoading = isValidateUserConnectLicenseActiveIdleOrLoading(state);
    this.initializeCartSuccess = isInitializeCartSuccess(state);
    this.initializeCartFailed = isInitializeCartFailed(state);

    if (isLoggedOutUser(state)) {
      this.isLoggedOut = true;
      this.stateIsDisabled = true;
    }

    if (isSuccessUser(state) && isValidateUserConnectLicenseActiveSuccess(state)) {
      this.validUserConnectLicense = true;
    }

    if (isSuccessUser(state) && isValidateUserConnectLicenseActiveFailed(state)) {
      this.stateIsDisabled = true;
      this.validUserConnectLicense = false;
      this.validateUserErrorMessage = validateUserConnectLicenseErrorMessage(state);
    }
    this.determineBtnText(state);

    if (this.initializeCartSuccess) {
      const redirectUrl: string = getCartPageUrl(this.user, this.store);
      doRedirect(redirectUrl);
    }
  }

  async determineBtnText(state: StoreState) {
    await waitForCart();
    if (getCartStatus(state) === ActionStatus.failed) {
      this.initializeCartFailed = true;
    }
  
    if (getCartStatus(state) === ActionStatus.succeeded) {
      const cartProductItem = shopGetCart(state)?.products?.find(
        (product) => product.productIdentifier.isbn10 === this.isbn13 || product.productIdentifier.isbn13 === this.isbn13
      );
      if (cartProductItem) {
        this.addedToCart = true;
        this.stateIsDisabled = true;
      }
      this.getButtonText();
    }
  }

  getButtonText() {
    this.addedToCart ? this.buttonText = "In Cart" : this.buttonText = 'Add To Cart';
  }

  renderComp() {
    return html`
      ${this.initializeCartFailed ? this.renderErrorToast() : nothing}
      ${this.renderInitializeCart()}
    `;
  }

  renderInitializeCart() {
    return html`
      <div class="row mb-3" tabindex="0">
        <div 
          class="col-5"
          aria-label="Price"
        >
          Price:
        </div>
        <div
          class="col-7 text-end fw-semibold"
          aria-label="${this.netPrice?.toFixed(2)!}"
        >
          $${this.netPrice?.toFixed(2)}
        </div>
      </div>
      <div class="row mb-3 border-1 border-top"></div>
      <div class="row mb-3" tabindex="0">
        <div
          class="col-7 fw-semibold"
          aria-label="Estimated Total"
        >
          <span>Estimated Total</span>:
        </div>
        <div
          class="d-flex col-5 justify-content-end fw-semibold"
          aria-label="${this.netPrice?.toFixed(2)!}"
        >
          $${this.netPrice?.toFixed(2)}
        </div>
      </div>
      <button
        class="btn btn-primary w-100"
        @click=${this._onClick}
        part="button"
        data-automation-id="add-to-cart-${this.isbn13}"
        ?disabled=${this.stateIsDisabled}
        tabindex="0"
      >
        ${this.isValidateUserIdleOrLoading ? smallSpinner() : this.buttonText}
      </button>

      <p
        class="fw-semibold mb-0 fs-small py-2"
        aria-label="${PRICING_SUBJECT_INFO}"
      >
        ${PRICING_SUBJECT_INFO}
      </p>

      ${this.isLoggedOut || !this.validUserConnectLicense ? html`
        <div class="mt-2">
          ${this.getUserErrorMessage()}
        </div>`
        : nothing
      }
    `;
  }

  private _onClick() {
    saveCartLocalStorageObject({
      programSourceId: this.programSourceId!,
      capturePDPLocalStorage: this.capturePDPLocalStorage,
      isbn10: this.isbn!,
      isbn13: this.isbn13!,
    });
    store
      .dispatch(initializeCart({
        countryCode: InitializeCartConstants.COUNTRY_CODE,
        currencyCode: InitializeCartConstants.CURRENCY_CODE,
        isbn: this.isbn13!,
        application: InitializeCartConstants.APPLICATION,
      }))
      .then(this.handleResponse.bind(this));
  }

  private renderErrorToast() {
    return html`
      <div class="initialize-cart-error-toast">
        <mhecomm-toast
          type="primary"
          header="Error Adding Item to Cart"
          .body=${this.getErrorMessage()}
          ?autoHide="${false}"
          @toast-close="${() => (this.initializeCartFailed = false)}"
        ></mhecomm-toast>
      </div>
    `;
  }

  private emitAddToCart() {
    emit({
      element: this,
      name: 'added-to-cart',
      detail: this.dataLayerDetail(),
      dataLayer: true,
    });
  }

  private async handleResponse() {
    await waitForCart();

    const state = store.getState() as StoreState;
    if (getCartStatus(state) === ActionStatus.succeeded) {
      this.emitAddToCart();
    }
  }

  private getErrorMessage(internalServerError = false) {
    return getApiErrorMessage({ bold: true, internalServerError });
  }

  private getUserErrorMessage() {
    return html`
      <div class="alert alert-danger mb-0">
        <div class="fw-bold">
          ${this.isLoggedOut ?
            html`
              <span
                data-automation-id="user-logged-out-error"
              >
                Please ${loginLink(this, false)} to add product to cart.
              </span>
            `
            : nothing
          }
          ${!this.validUserConnectLicense ? this.getUserErrorMessageContent() : nothing}
        </div>
      </div>
    `;
  }

  private getUserErrorMessageContent() {
    switch (this.validateUserErrorMessage) {
      case LoggedInUserValidateErrorConstants.NO_CONNECT_LICENSE:
        return html`
          <span data-automation-id="user-invalid-connect-license-error">
            This product requires a Connect license. Please ${loginLink(this, true)} to your Connect account to proceed.
          </span>
        `;
      case LoggedInUserValidateErrorConstants.LOOSELEAF_ALREADY_PURCHASED:
        return html`
          <span data-automation-id="product-already-purchased-error">
            You have already purchased this product.
          </span>
        `;
      default:
        return this.getErrorMessage(true);
    }
  }
  
  private dataLayerDetail() {
    const dataLayerProduct = window.adobeDataLayer?.getState?.()?.product;
    const state = store.getState() as StoreState;

    return captureAddToCartAnalytics({
      product: dataLayerProduct,
      state,
      isbn: this.isbn,
      productTitle: this.productTitle,
      businessUnit: this.businessUnit,
      copyrightYear: this.copyRightYear,
      instructorOnly: this.showRestrictions,
      isbn13: this.isbn13,
      netPrice: this.netPrice,
      description: this.description || '',
      gradeLevel: this.gradeLevel || '',
      quantity: this.quantity,
      store: this.store,
      contributors: this.contributors,
      edition: this.edition,
      pdpType: this.pdpType,
      dataset: this.dataset,
      showQuantity: true,
      isEdoms: false,
      isEvergreenProduct: this.everGreenProduct,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-initialize-cart': InitializeCart;
  }
}
